import { useRef, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Chip } from "@/components/ds";
import { useOrgAccessSettings, useUpdateOrgAccessSettings, useUpdateOrgChat } from "@/hooks/useOrgAccessSettings";
import { ADMIN_ONLY_CHIP, AdminOnlyNote } from "@/components/corporate/organizations/page/SectionFrame";
import { Gift, HeartPulse, MessageCircle, ShieldCheck, UserRoundX } from "lucide-react";

interface OrgAccessSettingsCardProps {
  /** Organization GUID — the settings are per organization. */
  organizationId: string;
  /** Chat is admin-only (OP-D7): an Athlyn team member sees it read-only. */
  canChangeChat: boolean;
}

interface AccessToggleRowProps {
  testId: string;
  title: string;
  /** Shown next to the title (e.g. the "Athlyn admins only" chip). */
  badge?: React.ReactNode;
  description: string;
  icon: React.ReactNode;
  checked: boolean;
  isLoading: boolean;
  isError: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Extra line under the description (e.g. the chat "Applying" note). */
  note?: React.ReactNode;
}

function AccessToggleRow({
  testId,
  title,
  badge,
  description,
  icon,
  checked,
  isLoading,
  isError,
  disabled,
  onCheckedChange,
  note,
}: AccessToggleRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-lg border" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg text-primary">{icon}</div>
        <div>
          <h4 className="flex flex-wrap items-center gap-2 font-medium">
            {title}
            {badge}
          </h4>
          <p className="text-sm text-muted-foreground">{description}</p>
          {note}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isLoading ? (
          <Skeleton className="h-6 w-11 rounded-full" />
        ) : isError ? null : (
          <Switch
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            aria-label={title}
          />
        )}
      </div>
    </div>
  );
}

/**
 * "Sign-in & access" — who may sign in to the organization, whether perks are on, whether the
 * app asks for health at sign-in, and whether employees have chat (turning chat off asks to confirm:
 * it mutes the organization's chats and revokes chat tokens — CS-E8).
 * Guests, perks and health onboarding save through `PUT /organizations/{id}/access-settings`, which
 * never carries `chatEnabled` or `checkInNewWaysEnabled` (a flag not sent keeps its value). Chat
 * saves through the admin-only `PUT /organizations/{id}/chat`; team members see it read-only.
 */
export function OrgAccessSettingsCard({ organizationId, canChangeChat }: OrgAccessSettingsCardProps) {
  const { data: settings, isLoading, isError } = useOrgAccessSettings(organizationId);
  const updateSettings = useUpdateOrgAccessSettings(organizationId);
  const updateChat = useUpdateOrgChat(organizationId);
  const [confirmChatOffOpen, setConfirmChatOffOpen] = useState(false);
  // One confirm per opening of the dialog: a double click lands twice while the dialog is still
  // closing (its button stays mounted during the close animation) — the second one is ignored.
  const chatOffConfirmedRef = useRef(false);

  const allowExternalUsers = settings?.allowExternalUsers ?? true;
  const perksEnabled = settings?.perksEnabled ?? true;
  // No stored row (or an older API without the field) ⇒ on, today's behaviour.
  const healthOnboardingEnabled = settings?.healthOnboardingEnabled ?? true;
  const chatEnabled = settings?.chatEnabled ?? true;
  // True until the Worker has muted/unmuted the chats after a switch (read-only, CS-E7).
  const chatSwitchInProgress = settings?.chatSwitchInProgress === true;
  // Never PUT while the current values are unknown — an access save carries the three flags it owns.
  const switchesDisabled = updateSettings.isPending || updateChat.isPending || !settings;
  const chatDisabled = switchesDisabled || !canChangeChat;

  const openConfirmChatOff = () => {
    chatOffConfirmedRef.current = false;
    setConfirmChatOffOpen(true);
  };

  const confirmChatOff = () => {
    if (chatOffConfirmedRef.current || chatDisabled) return;
    chatOffConfirmedRef.current = true;
    updateChat.mutate({ chatEnabled: false });
  };

  return (
    // Flush in its Settings section (no nested card): a small heading, then the rows.
    <section data-testid="sign-in-access-card" data-flush="true" className="space-y-3">
      <div>
        <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ds-ink">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          Sign-in &amp; access
        </h3>
        <p className="mt-0.5 text-[13px] text-ds-ink2">
          Who may sign in to this organization, whether perks are offered, whether the app asks for health at sign-in, and whether employees have chat.
        </p>
      </div>
      <div className="space-y-3">
        {isError && (
          <p className="text-sm text-destructive">
            Couldn&apos;t load the access settings. Refresh the page to try again.
          </p>
        )}

        <AccessToggleRow
          testId="external-users-feature-row"
          title="Allow external (guest) users"
          description="When off, this hides guest sign-in in the app and disables Public visibility in the client portal. Existing Public items stay Public."
          icon={<UserRoundX className="h-5 w-5" />}
          checked={allowExternalUsers}
          isLoading={isLoading}
          isError={isError}
          disabled={switchesDisabled}
          onCheckedChange={(checked) =>
            updateSettings.mutate({ allowExternalUsers: checked, perksEnabled, healthOnboardingEnabled })
          }
        />

        <AccessToggleRow
          testId="perks-enabled-feature-row"
          title="Perks enabled"
          description="When off, this hides perks everywhere for this organization — the app, the client portal and the API."
          icon={<Gift className="h-5 w-5" />}
          checked={perksEnabled}
          isLoading={isLoading}
          isError={isError}
          disabled={switchesDisabled}
          onCheckedChange={(checked) =>
            updateSettings.mutate({ allowExternalUsers, perksEnabled: checked, healthOnboardingEnabled })
          }
        />

        <AccessToggleRow
          testId="health-onboarding-feature-row"
          title="Health onboarding"
          description="When off, the app doesn't ask employees to set up health after they sign in, and hides the 'connect your health' invitations. It still asks when someone joins a challenge that needs health data."
          icon={<HeartPulse className="h-5 w-5" />}
          checked={healthOnboardingEnabled}
          isLoading={isLoading}
          isError={isError}
          disabled={switchesDisabled}
          onCheckedChange={(checked) =>
            updateSettings.mutate({ allowExternalUsers, perksEnabled, healthOnboardingEnabled: checked })
          }
        />

        <AccessToggleRow
          testId="chat-enabled-feature-row"
          title="Chat"
          badge={<Chip tone="neutral">{ADMIN_ONLY_CHIP}</Chip>}
          description="When off, employees of this organization have no chat: the app shows none, and Athlyn stops sending anything to the chat provider. Old messages are kept and come back if you turn it on again."
          icon={<MessageCircle className="h-5 w-5" />}
          checked={chatEnabled}
          isLoading={isLoading}
          isError={isError}
          disabled={chatDisabled}
          onCheckedChange={(checked) => {
            if (!canChangeChat) return;
            if (checked) {
              updateChat.mutate({ chatEnabled: true });
            } else {
              // Turning chat off mutes chats and revokes tokens — confirm first (CS-E8).
              openConfirmChatOff();
            }
          }}
          note={
            <>
              {chatSwitchInProgress && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="chat-switch-applying">
                  Applying — this takes a few minutes.
                </p>
              )}
              {!canChangeChat && (
                <div className="mt-1">
                  <AdminOnlyNote />
                </div>
              )}
            </>
          }
        />
      </div>

      <AlertDialog open={confirmChatOffOpen} onOpenChange={setConfirmChatOffOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn chat off for this organization?</AlertDialogTitle>
            <AlertDialogDescription>
              Within a few minutes, employees stop getting chat notifications and can no longer open chat. Nothing is deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={chatDisabled}
              onClick={confirmChatOff}
            >
              Turn chat off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
